import React, { useEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { rolesForPath } from '../config/routeAccess.js';
import Forbidden from '../pages/status/Forbidden.jsx';

// Auto-logout after this much inactivity (protects account access). Overridable
// via window.__XF_IDLE_MS__ for testing; defaults to 15 minutes.
const DEFAULT_IDLE_MS = 15 * 60 * 1000;
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'];

/**
 * Gate for authenticated routes. Redirects anonymous users to login, enforces
 * an inactivity timeout — after the idle threshold the session is ended and
 * the customer is returned to sign-in with an "expired" notice — and refuses a
 * route the signed-in role is not entitled to.
 *
 * The role rule comes from `routeAccess`, which reads the same `roles` field
 * the navigation filters on, so a link that is hidden is also blocked. Pages
 * used to make this judgement individually; most did, two did not, and a
 * patient could open the staff account manager by typing its address.
 */
export default function ProtectedRoute() {
  const { isAuthenticated, loading, logout, user } = useAuth();
  const location = useLocation();
  const timerRef = useRef(null);

  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const idleMs =
      (typeof window !== 'undefined' && Number(window.__XF_IDLE_MS__)) || DEFAULT_IDLE_MS;

    const expire = () => {
      // Flag the reason, then log out. Clearing the session makes this guard
      // redirect to /login on its own (a single navigation, avoiding a race),
      // and the login page reads the flag to show the expiry notice.
      try {
        sessionStorage.setItem('xf_session_expired', '1');
      } catch {
        /* ignore storage failures */
      }
      logout();
    };
    const reset = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(expire, idleMs);
    };

    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, reset));
    };
  }, [isAuthenticated, logout]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // A route with no declared rule is open to any signed-in user — the
  // deliberate default for shared pages such as My Account.
  const allowed = rolesForPath(location.pathname);
  if (allowed && !allowed.includes(user?.role)) {
    // Rendered in place rather than redirected to /403, so the address bar
    // still shows what was refused and the back button behaves.
    return <Forbidden />;
  }

  return <Outlet />;
}
